import { useShopwareContext } from '@shopware/composables';

export interface CategoryTreeItem {
    id: string;
    parentId?: string;
    afterCategoryId?: string | null;
    active?: boolean;
    customFields?: { nd_tweakwise_hide_in_category_tree?: boolean } | null;
    translated: {
        name: string;
    };
    seoUrls: Array<{ seoPathInfo: string }>;
    children?: CategoryTreeItem[];
}

/**
 * Sort categories by their afterCategoryId linked list.
 * Each category's afterCategoryId points to the sibling that comes before it.
 * The first category has afterCategoryId = null.
 */
function sortByLinkedList<T extends { id: string; afterCategoryId?: string | null }>(items: T[]): T[] {
    if (items.length <= 1) return items;

    const byAfterId = new Map<string | null, T>();
    for (const item of items) {
        byAfterId.set(item.afterCategoryId ?? null, item);
    }

    const sorted: T[] = [];
    // Start with the item that has no predecessor (afterCategoryId = null or not pointing to any sibling)
    let current = byAfterId.get(null);
    if (!current) {
        // Fallback: find the item whose afterCategoryId doesn't reference any sibling
        const siblingIds = new Set(items.map(i => i.id));
        current = items.find(i => !i.afterCategoryId || !siblingIds.has(i.afterCategoryId));
    }

    const visited = new Set<string>();
    while (current && !visited.has(current.id)) {
        visited.add(current.id);
        sorted.push(current);
        // Find the next item: the one whose afterCategoryId points to current
        current = byAfterId.get(current.id);
    }

    // Append any items not reached by the chain (safety net)
    for (const item of items) {
        if (!visited.has(item.id)) sorted.push(item);
    }

    return sorted;
}

/**
 * Fetch a single category by ID with its children (+ nested children + seoUrls).
 * Pure async function — no internal refs, safe for useAsyncData.
 */
async function fetchCategoryById(apiClient: any, categoryId: string): Promise<CategoryTreeItem | null> {
    try {
        const { data } = await apiClient.invoke('Category POST /category', {
            body: {
                filter: [
                    {
                        type: 'equals',
                        field: 'id',
                        value: categoryId,
                    },
                ],
                associations: {
                    seoUrls: {},
                    children: {
                        associations: {
                            seoUrls: {},
                            children: {
                                associations: {
                                    seoUrls: {},
                                },
                            },
                        },
                    },
                },
            },
        });
        return data.elements?.[0] || null;
    } catch {
        return null;
    }
}

/**
 * Build the category tree (sibling list) for a given category.
 * Returns a plain Category[] array — designed to be used inside useAsyncData
 * so Nuxt handles SSR→client hydration automatically.
 *
 * @param category - The current category (must have at least `id` and `parentId`)
 * @param injectedApiClient - Optional pre-captured apiClient to avoid calling useShopwareContext() inside async handlers
 * @returns Category[] — the sibling tree with expanded children for the current path
 */
export async function fetchCategoryTree(category: { id: string; parentId?: string }, injectedApiClient?: any): Promise<CategoryTreeItem[]> {
    const apiClient = injectedApiClient ?? useShopwareContext().apiClient;

    if (!category?.parentId) {
        return [];
    }

    // Step 1: Fetch the parent category (includes its children via associations)
    const parent = await fetchCategoryById(apiClient, category.parentId);
    if (!parent) {
        return [];
    }

    // Step 2: Fetch the grandparent to determine hierarchy level
    const grandparent = parent.parentId ? await fetchCategoryById(apiClient, parent.parentId) : null;
    const isParentTopLevel = !grandparent?.parentId;

    if (isParentTopLevel) {
        // Parent is top-level (e.g., "Dames") — use parent's children as siblings
        const siblings = sortByLinkedList((parent.children || []).filter(sibling => sibling.active));

        const currentSibling = siblings.find(s => s.id === category.id);
        const currentChildren = sortByLinkedList(
            (currentSibling?.children || []).filter(c => c.active),
        );

        return siblings.map(sibling => ({
            ...sibling,
            children: sibling.id === category.id ? currentChildren : [],
        }));
    }

    // Parent is not top-level (e.g., "Broeken" under "Dames")
    if (!grandparent) return [];
    const grandparentChildren = sortByLinkedList((grandparent.children || []).filter(sibling => sibling.active));

    const parentChildren = sortByLinkedList((parent.children || []).filter(child => child.active));

    return grandparentChildren.map(sibling => {
        if (sibling.id === parent.id) {
            return {
                ...sibling,
                children: parentChildren.map(child => ({
                    ...child,
                    children: child.id === category.id
                        ? sortByLinkedList((child.children || []).filter(c => c.active))
                        : [],
                })),
            };
        }
        return {
            ...sibling,
            children: [],
        };
    });
}
